import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
  createTask,
  Task,
  TaskAssigneeType,
  TaskCategory,
  TaskPayload,
  TaskStatus,
  updateTask,
} from "../../../services/task.service";
import { getLoginUserData } from "../../../services/account.service";
import { getEmployees } from "../../../services/employee.service";
import { getCoOwners } from "../../../services/co-owner.service";

interface AssigneeOption {
  id: string;
  label: string;
  email: string;
}

type LinkedLead = {
  id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  businessName?: string;
  email?: string | null;
};

type LinkedDeal = {
  id: string;
  loanName?: string;
  dealType?: string;
  loanSize?: number;
  status?: string;
};

type TaskFormDialogProps = {
  open: boolean;
  onClose: (action?: string) => void;
  editTask?: Task | null;
  lead?: LinkedLead | null;
  deal?: LinkedDeal | null;
};

const DEFAULT_FORM: TaskPayload = {
  title: "",
  description: "",
  category: "Lead",
  status: "To Do",
  followUpDate: "",
  assigneeType: "Unassigned",
  assigneeId: "",
  leadId: null,
  dealId: null,
};

const categoryOptions: TaskCategory[] = ["Lead", "Deal", "Other"];
const statusOptions: TaskStatus[] = ["To Do", "In Progress", "Completed"];
const assigneeTypeOptions: TaskAssigneeType[] = ["Unassigned", "Employee", "Co-owner"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
  },
};

const formatName = (person: any) =>
  [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ");

const getLeadName = (lead?: LinkedLead | null) =>
  lead ? formatName(lead) || lead.email || "Selected lead" : "";

const getDealName = (deal?: LinkedDeal | null) =>
  deal?.loanName || "Selected deal";

const TaskFormDialog: React.FC<TaskFormDialogProps> = ({
  open,
  onClose,
  editTask,
  lead,
  deal,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [isSaving, setIsSaving] = useState(false);
  const [employees, setEmployees] = useState<AssigneeOption[]>([]);
  const [coOwners, setCoOwners] = useState<AssigneeOption[]>([]);
  const [form, setForm] = useState<TaskPayload>(DEFAULT_FORM);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  const linkedLead = lead || editTask?.lead || null;
  const linkedDeal = deal || editTask?.deal || null;
  const isCategoryLocked = !!lead || !!deal;

  const assigneeOptions = useMemo(() => {
    if (form.assigneeType === "Employee") return employees;
    if (form.assigneeType === "Co-owner") return coOwners;
    return [];
  }, [coOwners, employees, form.assigneeType]);

  const showSnackbar = (
    severity: "success" | "error" | "info",
    message: string
  ) => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    if (!open) return;

    const loadAssignees = async () => {
      try {
        const user = await getLoginUserData();
        const businessId = user?.businessId || user?.id;
        const [employeeRes, coOwnerRes] = await Promise.all([
          getEmployees({ page: 1, limit: 100, search: "", businessId }),
          getCoOwners({ page: 1, limit: 100, search: "", businessId }),
        ]);

        setEmployees(
          (employeeRes?.data || []).map((employee: any) => ({
            id: employee.id,
            label: formatName(employee),
            email: employee.email,
          }))
        );
        setCoOwners(
          (coOwnerRes?.data || []).map((coOwner: any) => ({
            id: coOwner.id,
            label: formatName(coOwner),
            email: coOwner.email,
          }))
        );
      } catch (error: any) {
        showSnackbar("error", error?.message || "Failed to load assignees.");
      }
    };

    loadAssignees();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (editTask) {
      setForm({
        title: editTask.title,
        description: editTask.description || "",
        category: editTask.category,
        status: editTask.status,
        followUpDate: editTask.followUpDate ? editTask.followUpDate.slice(0, 10) : "",
        assigneeType: editTask.assigneeType,
        assigneeId: editTask.assigneeId || "",
        leadId: editTask.leadId || editTask.lead?.id || null,
        dealId: editTask.dealId || editTask.deal?.id || null,
      });
      return;
    }

    setForm({
      ...DEFAULT_FORM,
      category: lead ? "Lead" : deal ? "Deal" : DEFAULT_FORM.category,
      leadId: lead?.id || null,
      dealId: deal?.id || null,
    });
  }, [deal, editTask, lead, open]);

  const handleFormChange = (field: keyof TaskPayload, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "assigneeType" ? { assigneeId: "" } : {}),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showSnackbar("error", "Title is required.");
      return;
    }

    if (form.assigneeType !== "Unassigned" && !form.assigneeId) {
      showSnackbar("error", "Please choose an assignee.");
      return;
    }

    setIsSaving(true);
    try {
      const payload: TaskPayload = {
        ...form,
        category: lead ? "Lead" : deal ? "Deal" : form.category,
        leadId: lead?.id || form.leadId || null,
        dealId: deal?.id || form.dealId || null,
        title: form.title.trim(),
        description: form.description?.trim(),
        followUpDate: form.followUpDate || null,
        assigneeId: form.assigneeType === "Unassigned" ? null : form.assigneeId,
      };

      if (editTask) {
        await updateTask(editTask.id, payload);
        showSnackbar("success", "Task updated successfully.");
      } else {
        await createTask(payload);
        showSnackbar("success", "Task added successfully.");
      }

      onClose("reload");
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to save task.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose("cancel");
        }
      }}
      fullScreen={isSmallScreen}
      maxWidth={isSmallScreen ? false : "md"}
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          height: isSmallScreen ? "100vh" : "auto",
          maxHeight: isSmallScreen ? "100vh" : "none",
          m: isSmallScreen ? 0 : undefined,
          borderRadius: isSmallScreen ? 0 : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="span" className="font-bold text-gray-800">
          {editTask ? "Edit Task" : "Add Task"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => onClose("cancel")}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <DialogContent className="custom-scrollbar" sx={{ flex: 1, overflowY: "auto" }}>
          <div className="space-y-4 py-2">
            <Grid container spacing={2}>
              {linkedLead && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Lead"
                    value={`${getLeadName(linkedLead)}${linkedLead.businessName ? ` - ${linkedLead.businessName}` : ""}`}
                    fullWidth
                    disabled
                    sx={fieldSx}
                  />
                </Grid>
              )}
              {linkedDeal && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Deal"
                    value={getDealName(linkedDeal)}
                    fullWidth
                    disabled
                    sx={fieldSx}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Title *"
                  value={form.title}
                  onChange={(event) => handleFormChange("title", event.target.value)}
                  fullWidth
                  placeholder="Enter task title"
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Follow Up Date"
                  type="date"
                  value={form.followUpDate || ""}
                  onChange={(event) => handleFormChange("followUpDate", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={form.category}
                    disabled={isCategoryLocked}
                    onChange={(event) => handleFormChange("category", event.target.value)}
                  >
                    {categoryOptions.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={(event) => handleFormChange("status", event.target.value)}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Assign Type</InputLabel>
                  <Select
                    label="Assign Type"
                    value={form.assigneeType}
                    onChange={(event) =>
                      handleFormChange("assigneeType", event.target.value)
                    }
                  >
                    {assigneeTypeOptions.map((assigneeType) => (
                      <MenuItem key={assigneeType} value={assigneeType}>
                        {assigneeType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {form.assigneeType !== "Unassigned" && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>Assign To</InputLabel>
                    <Select
                      label="Assign To"
                      value={form.assigneeId || ""}
                      onChange={(event) =>
                        handleFormChange("assigneeId", event.target.value)
                      }
                    >
                      {assigneeOptions.map((assignee) => (
                        <MenuItem key={assignee.id} value={assignee.id}>
                          {assignee.label} - {assignee.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(event) => handleFormChange("description", event.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Enter task details"
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </div>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
          }}
        >
          <Button
            color="error"
            variant="outlined"
            onClick={() => onClose("cancel")}
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              isSaving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {editTask ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default TaskFormDialog;
